import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { FILE_ANNOTATIONS_QUERY, SHARED_FILE_ANNOTATIONS_QUERY, SHARED_NOTES_ACCESS_QUERY, SCENE_SETTINGS_QUERY } from '../../graphql/queries';
import {
  CREATE_ANNOTATION_MUTATION,
  REPLY_ANNOTATION_MUTATION,
  RESOLVE_ANNOTATION_MUTATION,
  DELETE_ANNOTATION_MUTATION,
  ADD_ANNOTATION_ATTACHMENT_MUTATION,
  DELETE_ANNOTATION_ATTACHMENT_MUTATION,
  LINK_ANNOTATION_FILE_MUTATION,
  UNLINK_ANNOTATION_FILE_MUTATION,
  SHARED_CREATE_ANNOTATION_MUTATION,
  SHARED_REPLY_ANNOTATION_MUTATION,
} from '../../graphql/mutations';
import { putWithProgress } from '../../utils/putWithProgress';
import { LinkedFile, linkedFileIcon, linkedFileRoute } from '../../utils/linkedFiles';
import { useDocumentFormats } from '../../hooks/useDocumentFormats';
import { splatViewerSwitches } from '../../utils/splatViewerUrl';
import NoteFilePicker from '../../components/common/NoteFilePicker';
import styles from './SplatViewer.module.css';
import Icon from '../../components/common/Icon';

interface AnnotationPosition { x: number; y: number; z: number }
interface AnnotationReply {
  id: string;
  text: string;
  createdAt: string;
  createdBy: { firstName: string; lastName: string };
  guestName?: string | null;
}
interface NoteAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  createdAt: string;
  url: string | null;
  downloadUrl: string | null;
  uploadedBy: { firstName: string; lastName: string } | null;
}
interface SplatAnnotation {
  id: string;
  text: string;
  status: string;
  createdAt: string;
  position: AnnotationPosition;
  createdBy: { firstName: string; lastName: string };
  guestName?: string | null;
  replies: AnnotationReply[];
  attachments: NoteAttachment[];
  linkedFiles: LinkedFile[];
}

// Share-link guests write under the link creator's account; their own name
// is what's shown.
const authorName = (x: { guestName?: string | null; createdBy: { firstName: string; lastName: string } }) =>
  x.guestName ? `${x.guestName} (guest)` : `${x.createdBy.firstName} ${x.createdBy.lastName}`;

const GUEST_NAME_KEY = 'geodraft_guest_name';

const formatTime = (s: string) => new Date(s).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formatBytes = (b: number) =>
  b < 1024 ? `${b} B`
    : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB`
      : `${(b / (1024 * 1024)).toFixed(1)} MB`;

// Notes accept PDFs only (the backend enforces the same rule and checks the
// file's %PDF- signature); older non-PDF test attachments still list and
// download, they just get no inline preview.
const PDF_MIME = 'application/pdf';
const isPdfFile = (file: File) => file.type === PDF_MIME || /\.pdf$/i.test(file.name);
const isPdfAttachment = (att: NoteAttachment) => att.mimeType === PDF_MIME || /\.pdf$/i.test(att.fileName);

interface SplatViewerProps {
  /** Combined View embeds the viewer as a pane component. */
  fileId?: string;
  embedded?: boolean;
  /** The Scene's base pane: no rail of its own — the Scene rail lists scenes
      and notes (through onInventoryChange + the handle), the top bar keeps
      only the "Note" action and a floating panel shows the form / one note. */
  sceneMode?: boolean;
  onInventoryChange?: (inv: SplatInventory) => void;
  /** Embedded viewers have no :projectId route param — the Scene passes it. */
  projectId?: string;
  onOpenLinkedFile?: (file: LinkedFile) => void;
}

export interface SplatInventory {
  scenes: { url: string; label: string; active: boolean }[];
  notes: { id: string; label: string; visible: boolean }[];
}

export interface SplatViewerHandle {
  setScene(url: string): void;
  setNoteVisible(id: string, visible: boolean): void;
  focusNote(id: string): void;
}

// Photoreal (Gaussian splat) viewer — Combined View layout: slim topbar,
// left rail (Scenes + Notes sections), viewer pane. The WebGL renderer lives
// in the /gs-viewer.html iframe and is driven over the same postMessage
// marker protocol the Potree viewer uses. Standalone route by default;
// Combined View passes fileId/embedded props instead.
const SplatViewer = forwardRef<SplatViewerHandle, SplatViewerProps>(function SplatViewer(
  { fileId: propFileId, embedded, sceneMode, onInventoryChange, projectId: propProjectId, onOpenLinkedFile },
  handleRef,
) {
  const { projectId: routeProjectId, fileId: routeFileId, token: shareToken } = useParams<{ projectId: string; fileId: string; token?: string }>();
  const projectId = propProjectId ?? routeProjectId;
  const fileId = propFileId ?? routeFileId;
  // Public share link (/share/:token/viewer-splat/:fileId): view-only — notes
  // need an account, exactly like the shared Smart View.
  const isShare = !!shareToken;
  const navigate = useNavigate();
  const [sceneUrl, setSceneUrl] = useState<string | null>(null);
  const [zones, setZones] = useState<{ label: string; url: string }[]>([]);
  const [tiles, setTiles] = useState<{
    lodCell?: number;
    midCell?: number;
    entries: { c: number[]; r: number; n: number; o?: number[]; mid?: { url: string; n: number }; url: string }[];
  } | null>(null);
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [annotationMode, setAnnotationMode] = useState(false);
  const [pendingPos, setPendingPos] = useState<AnnotationPosition | null>(null);
  const [pendingText, setPendingText] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  const [hiddenNotes, setHiddenNotes] = useState<Set<string>>(() => new Set());
  const activeCardRef = useRef<HTMLDivElement>(null);
  const [uploads, setUploads] = useState<Record<string, { annotationId: string; name: string; pct: number }>>({});
  const [attachError, setAttachError] = useState('');
  const [viewing, setViewing] = useState<NoteAttachment | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const attachTargetRef = useRef<string | null>(null);

  const post = useCallback((msg: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*');
  }, []);

  // The navigation pace (Platform Settings → Scene) goes to the renderer by
  // message once it has loaded, as in the Scene — never in the iframe URL.
  const { data: sceneSettingsData } = useQuery(SCENE_SETTINGS_QUERY);
  useEffect(() => {
    const s = sceneSettingsData?.sceneSettings;
    if (!loaded || !s) return;
    post({ type: 'setSceneSettings', walkMps: s.walkSpeedMps, runMultiplier: s.runMultiplier, distanceFactor: s.distanceFactor });
  }, [loaded, sceneSettingsData, post]);

  const { data: annotationsData, refetch: refetchOwnNotes } = useQuery(FILE_ANNOTATIONS_QUERY, {
    variables: { fileId },
    skip: !fileId || isShare,
    fetchPolicy: 'cache-and-network',
  });
  const { data: sharedNotesData, refetch: refetchSharedNotes } = useQuery(SHARED_FILE_ANNOTATIONS_QUERY, {
    variables: { token: shareToken, fileId },
    skip: !fileId || !isShare,
    fetchPolicy: 'cache-and-network',
  });
  const annotations: SplatAnnotation[] =
    (isShare ? sharedNotesData?.sharedFileAnnotations : annotationsData?.fileAnnotations) ?? [];
  const refetchAnnotations = async (): Promise<SplatAnnotation[]> => {
    if (isShare) {
      const res = await refetchSharedNotes();
      return res.data?.sharedFileAnnotations ?? [];
    }
    const res = await refetchOwnNotes();
    return res.data?.fileAnnotations ?? [];
  };

  // Share-link visitors may write notes only where the link creator allowed it.
  const { data: accessData } = useQuery(SHARED_NOTES_ACCESS_QUERY, {
    variables: { token: shareToken, fileId },
    skip: !isShare || !fileId,
    fetchPolicy: 'cache-and-network',
  });
  const canWriteNotes = !isShare || !!accessData?.sharedNotesAccess;
  const [guestName, setGuestName] = useState(() => {
    try { return localStorage.getItem(GUEST_NAME_KEY) || ''; } catch { return ''; }
  });
  const updateGuestName = (v: string) => {
    setGuestName(v);
    try { localStorage.setItem(GUEST_NAME_KEY, v); } catch { /* per-browser convenience only */ }
  };
  const [noteError, setNoteError] = useState('');
  const onNoteError = (err: { message: string }) => setNoteError(err.message);

  const onCreated = () => { setPendingPos(null); setPendingText(''); setNoteError(''); refetchAnnotations(); };
  const onReplied = () => { setReplyText(''); setNoteError(''); refetchAnnotations(); };
  const [createAnnotation, { loading: creatingOwn }] = useMutation(CREATE_ANNOTATION_MUTATION, {
    onCompleted: onCreated,
    onError: onNoteError,
  });
  const [replyToAnnotation, { loading: replyingOwn }] = useMutation(REPLY_ANNOTATION_MUTATION, {
    onCompleted: onReplied,
    onError: onNoteError,
  });
  const [sharedCreateAnnotation, { loading: creatingGuest }] = useMutation(SHARED_CREATE_ANNOTATION_MUTATION, {
    onCompleted: onCreated,
    onError: onNoteError,
  });
  const [sharedReplyToAnnotation, { loading: replyingGuest }] = useMutation(SHARED_REPLY_ANNOTATION_MUTATION, {
    onCompleted: onReplied,
    onError: onNoteError,
  });
  const creating = creatingOwn || creatingGuest;
  const replying = replyingOwn || replyingGuest;
  const [resolveAnnotation] = useMutation(RESOLVE_ANNOTATION_MUTATION, {
    onCompleted: () => refetchAnnotations(),
  });
  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION_MUTATION, {
    onCompleted: () => { setExpandedId(null); refetchAnnotations(); },
  });
  const [addAttachment] = useMutation(ADD_ANNOTATION_ATTACHMENT_MUTATION);
  const [deleteAttachment] = useMutation(DELETE_ANNOTATION_ATTACHMENT_MUTATION);
  const [linkPickerFor, setLinkPickerFor] = useState<string | null>(null);
  const [linkFile, { loading: linkingFile }] = useMutation(LINK_ANNOTATION_FILE_MUTATION, {
    onCompleted: () => { setLinkPickerFor(null); refetchAnnotations(); },
    onError: onNoteError,
  });
  const [unlinkFile] = useMutation(UNLINK_ANNOTATION_FILE_MUTATION, {
    onCompleted: () => refetchAnnotations(),
    onError: onNoteError,
  });
  const documentFormats = useDocumentFormats();
  const openLinkedFile = (file: LinkedFile) => {
    if (onOpenLinkedFile) { onOpenLinkedFile(file); return; }
    const route = linkedFileRoute(file, projectId, shareToken, documentFormats);
    if (route) navigate(route);
  };

  // Upload flow: presign (REST) → browser PUT straight to S3 → record it
  // (GraphQL). Localhost can't PUT to the bucket (CORS), so a failed direct
  // PUT retries through the backend's piped pass-through.
  const uploadAttachments = async (annotationId: string, fileList: FileList) => {
    setAttachError('');
    const token = localStorage.getItem('geodraft_token') || '';
    const all = Array.from(fileList);
    const pdfs = all.filter(isPdfFile);
    const skipped = all.filter((f) => !isPdfFile(f));
    if (skipped.length > 0) {
      setAttachError(`Only PDF files can be attached — skipped ${skipped.map((f) => f.name).join(', ')}`);
    }
    for (const file of pdfs) {
      const key = crypto.randomUUID();
      setUploads((u) => ({ ...u, [key]: { annotationId, name: file.name, pct: 0 } }));
      try {
        const presignRes = await fetch(`/api/annotations/${annotationId}/attachments/presign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fileName: file.name, fileSize: file.size }),
        });
        const presign = await presignRes.json().catch(() => ({}));
        if (!presignRes.ok) throw new Error(presign.error || 'Could not prepare the upload');
        const { s3Key, uploadUrl } = presign as { s3Key: string; uploadUrl: string };
        try {
          await putWithProgress(uploadUrl, file, (pct) =>
            setUploads((u) => (u[key] ? { ...u, [key]: { ...u[key], pct } } : u)), PDF_MIME);
        } catch {
          const proxyRes = await fetch(`/api/annotations/attachments/upload?key=${encodeURIComponent(s3Key)}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': PDF_MIME },
            body: file,
          });
          if (!proxyRes.ok) {
            const body = await proxyRes.json().catch(() => ({}));
            throw new Error(body.error || 'Upload failed');
          }
        }
        await addAttachment({ variables: { annotationId, s3Key, fileName: file.name } });
      } catch (e) {
        setAttachError(`${file.name}: ${(e as Error).message}`);
      } finally {
        setUploads((u) => {
          const next = { ...u };
          delete next[key];
          return next;
        });
      }
    }
    refetchAnnotations();
  };

  // Presigned URLs expire after an hour — always refetch before opening one.
  const openAttachment = async (annotationId: string, attachmentId: string, mode: 'view' | 'download') => {
    setAttachError('');
    try {
      const list = await refetchAnnotations();
      const fresh = list.find((x) => x.id === annotationId)?.attachments.find((x) => x.id === attachmentId);
      if (!fresh) { setAttachError('That file no longer exists'); return; }
      if (mode === 'download') {
        if (fresh.downloadUrl) window.location.assign(fresh.downloadUrl);
        else setAttachError(isShare ? 'This link does not allow downloads' : 'Downloads need S3 storage');
        return;
      }
      setViewing(fresh);
    } catch (e) {
      setAttachError((e as Error).message);
    }
  };

  const removeAttachment = async (annotationId: string, att: NoteAttachment) => {
    if (!window.confirm(`Remove "${att.fileName}" from this note?`)) return;
    setAttachError('');
    try {
      await deleteAttachment({ variables: { annotationId, attachmentId: att.id } });
    } catch (e) {
      setAttachError((e as Error).message);
    }
  };

  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  useEffect(() => {
    if (!fileId) return;
    const load = async () => {
      try {
        const r = isShare
          ? await fetch(`/api/files/${fileId}/gaussian-url?shareToken=${encodeURIComponent(shareToken!)}`)
          : await fetch(`/api/files/${fileId}/gaussian-url`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('geodraft_token') || ''}` },
          });
        if (!r.ok) {
          setError(isShare && r.status === 404
            ? 'This share link is no longer available.'
            : 'Failed to load the Gaussian splat scene.');
          return;
        }
        const d = await r.json();
        if (d?.url) {
          setSceneUrl(d.url);
          setActiveUrl(d.url);
          setZones(Array.isArray(d.zones) ? d.zones : []);
          if (d.tiles?.entries?.length) setTiles(d.tiles);
        } else setError('This Gaussian splat is not ready yet.');
      } catch {
        setError('Failed to load the Gaussian splat scene.');
      }
    };
    load();
  }, [fileId, isShare, shareToken]);

  // The renderer registers its listener only after the base scene finishes
  // downloading: post the tile list the moment it reports the scene loaded
  // (on a blind 1.5 s retry alone, streaming started up to 1.5 s late), and
  // keep re-posting until it acknowledges in case that report was missed.
  useEffect(() => {
    if (!tiles || !activeUrl || activeUrl !== sceneUrl) return;
    let acked = false;
    const send = () => post({ type: 'splat-tiles', entries: tiles.entries, lodCell: tiles.lodCell, midCell: tiles.midCell });
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'splat-tiles-ack') acked = true;
      else if (e.data?.type === 'splat-loaded' && !acked) send();
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(() => {
      if (acked) { clearInterval(timer); return; }
      send();
    }, 1500);
    return () => { clearInterval(timer); window.removeEventListener('message', onMessage); };
  }, [tiles, activeUrl, sceneUrl, post]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const d = e.data;
      if (!d || typeof d.type !== 'string') return;
      if (d.type === 'splat-loaded') setLoaded(true);
      if (d.type === 'pointPicked' && d.position) {
        setPendingPos(d.position as AnnotationPosition);
        setAnnotationMode(false);
        setNotesCollapsed(false);
        post({ type: 'setAnnotationMode', enabled: false });
      }
      if (d.type === 'annotationCancelled') setAnnotationMode(false);
      if (d.type === 'markerClicked') {
        const id = d.id as string;
        setActiveId(id);
        setExpandedId(id);
        setNotesCollapsed(false);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [post]);

  // Zone switches remount the iframe — reset per-scene viewer state.
  useEffect(() => { setLoaded(false); setAnnotationMode(false); }, [activeUrl]);

  useEffect(() => {
    if (!annotationMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAnnotationMode(false);
        post({ type: 'setAnnotationMode', enabled: false });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [annotationMode, post]);

  // Sync annotation markers into the viewer (whole-scene view only — zone
  // splats are cropped sub-scenes and markers would float over missing walls).
  const showAnnotations = activeUrl === sceneUrl;
  useEffect(() => {
    if (!loaded) return;
    post({ type: 'clearMarkers' });
    if (!showAnnotations) return;
    annotations.forEach((a, i) => {
      if (hiddenNotes.has(a.id)) return;
      post({ type: 'addMarker', id: a.id, position: a.position, index: i + 1, status: a.status, active: a.id === activeId });
    });
  }, [annotations, loaded, post, activeId, showAnnotations, hiddenNotes]);

  useImperativeHandle(handleRef, () => ({
    setScene: (url: string) => setActiveUrl(url),
    setNoteVisible: (id: string, visible: boolean) => setHiddenNotes((prev) => {
      if (visible === !prev.has(id)) return prev;
      const next = new Set(prev);
      if (visible) next.delete(id); else next.add(id);
      return next;
    }),
    focusNote: (id: string) => {
      setActiveId(id);
      setExpandedId(id);
      post({ type: 'highlightMarker', id });
    },
  }), [post]);

  const inventorySigRef = useRef('');
  useEffect(() => {
    if (!onInventoryChange) return;
    const inv: SplatInventory = {
      scenes: zones.length > 0 && sceneUrl
        ? [{ url: sceneUrl, label: 'Overview', active: activeUrl === sceneUrl },
           ...zones.map((z) => ({ url: z.url, label: z.label, active: activeUrl === z.url }))]
        : [],
      notes: annotations.map((a) => ({ id: a.id, label: a.text, visible: !hiddenNotes.has(a.id) })),
    };
    const sig = JSON.stringify(inv);
    if (sig === inventorySigRef.current) return;
    inventorySigRef.current = sig;
    onInventoryChange(inv);
  });

  useEffect(() => {
    if (activeId && activeCardRef.current) {
      activeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeId]);

  const startAddNote = () => {
    setAnnotationMode(true);
    post({ type: 'setAnnotationMode', enabled: true });
  };
  const cancelAddNote = () => {
    setAnnotationMode(false);
    post({ type: 'setAnnotationMode', enabled: false });
  };
  const handleSaveAnnotation = () => {
    if (!fileId || !pendingPos || !pendingText.trim()) return;
    setNoteError('');
    if (isShare) {
      sharedCreateAnnotation({ variables: {
        token: shareToken, fileId, position: pendingPos, text: pendingText.trim(), guestName: guestName.trim() || null,
      } });
    } else {
      createAnnotation({ variables: { fileId, position: pendingPos, text: pendingText.trim() } });
    }
  };
  const handleReply = (annotationId: string) => {
    if (!replyText.trim()) return;
    setNoteError('');
    if (isShare) {
      sharedReplyToAnnotation({ variables: {
        token: shareToken, annotationId, text: replyText.trim(), guestName: guestName.trim() || null,
      } });
    } else {
      replyToAnnotation({ variables: { annotationId, text: replyText.trim() } });
    }
  };
  const handleCardClick = (id: string) => {
    setActiveId(id);
    setExpandedId((prev) => (prev === id ? null : id));
    post({ type: 'highlightMarker', id });
  };

  const noteFormBlock = (
    <>
              {annotationMode && (
                <div className={styles.noteForm}>
                  <div className={styles.noteHint}>
                    <Icon name="ads_click" style={{ fontSize: 14 }} />
                    Click on the scene to place the note
                  </div>
                  <div className={styles.btnRow}>
                    <button className={styles.btnGhost} onClick={cancelAddNote}>Cancel</button>
                  </div>
                </div>
              )}
              {pendingPos && (
                <div className={styles.noteForm}>
                  <div className={styles.noteHint}>
                    <Icon name="location_on" style={{ fontSize: 14 }} />
                    Position captured — add a comment
                  </div>
                  {isShare && (
                    <input
                      className={styles.textarea}
                      placeholder="Your name (shown on the note)"
                      value={guestName}
                      maxLength={60}
                      onChange={(e) => updateGuestName(e.target.value)}
                    />
                  )}
                  <textarea
                    className={styles.textarea}
                    placeholder="Describe the issue or note…"
                    value={pendingText}
                    onChange={(e) => setPendingText(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <div className={styles.btnRow}>
                    <button className={styles.btnGhost} onClick={() => { setPendingPos(null); setPendingText(''); }}>
                      Cancel
                    </button>
                    <div style={{ flex: 1 }} />
                    <button
                      className={styles.btnPrimary}
                      onClick={handleSaveAnnotation}
                      disabled={!pendingText.trim() || creating}
                    >
                      {creating ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {noteError && <p className={styles.attachError}>{noteError}</p>}
                </div>
              )}
    </>
  );

  const renderNoteCard = (a: SplatAnnotation, i: number) => (
                <div
                  key={a.id}
                  ref={a.id === activeId ? activeCardRef : null}
                  className={`${styles.noteCard} ${a.id === activeId ? styles.noteCardActive : ''} ${a.status === 'resolved' ? styles.noteCardResolved : ''}`}
                  onClick={() => handleCardClick(a.id)}
                >
                  <div className={styles.noteHead}>
                    <span className={styles.noteIdx}>{String(i + 1).padStart(2, '0')}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className={styles.noteAuthor}>{authorName(a)}</p>
                      <p className={styles.noteTime}>{formatTime(a.createdAt)}</p>
                    </div>
                    {a.attachments.length > 0 && (
                      <span className={styles.noteAttachChip} title={`${a.attachments.length} attached file(s)`}>
                        <Icon name="attach_file" style={{ fontSize: 12 }} />
                        {a.attachments.length}
                      </span>
                    )}
                    {a.linkedFiles.length > 0 && (
                      <span className={styles.noteAttachChip} title={`${a.linkedFiles.length} linked file(s)`}>
                        <Icon name="link" style={{ fontSize: 12 }} />
                        {a.linkedFiles.length}
                      </span>
                    )}
                    {a.status === 'resolved' && <span className={styles.noteResolvedChip}>Resolved</span>}
                  </div>
                  <p className={styles.noteText}>{a.text}</p>
                  {a.replies.length > 0 && (
                    <div className={styles.noteReplies}>
                      {a.replies.map((r) => (
                        <div key={r.id}>
                          <p className={styles.replyAuthor}>{authorName(r)}</p>
                          <p className={styles.replyText}>{r.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {expandedId === a.id && (
                    <div className={styles.noteActions} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.attachBlock}>
                        <div className={styles.attachHeader}>
                          <span className={styles.attachLabel}>Files</span>
                          {!isShare && (
                            <button
                              className={styles.attachAddBtn}
                              onClick={() => { attachTargetRef.current = a.id; attachInputRef.current?.click(); }}
                            >
                              <Icon name="attach_file" style={{ fontSize: 13 }} />
                              Attach PDF
                            </button>
                          )}
                        </div>
                        {a.attachments.map((att) => {
                          return (
                            <div key={att.id} className={styles.attachRow}>
                              <Icon name={isPdfAttachment(att) ? 'picture_as_pdf' : 'draft'} className={styles.attachIcon} />
                              <button
                                className={styles.attachName}
                                onClick={() => openAttachment(a.id, att.id, 'view')}
                                title={`View ${att.fileName}`}
                              >
                                {att.fileName}
                              </button>
                              <span className={styles.attachSize}>{formatBytes(att.fileSize)}</span>
                              {att.downloadUrl && (
                                <button
                                  className={styles.attachIconBtn}
                                  onClick={() => openAttachment(a.id, att.id, 'download')}
                                  title="Download"
                                >
                                  <Icon name="download" style={{ fontSize: 14 }} />
                                </button>
                              )}
                              {!isShare && (
                                <button
                                  className={styles.attachIconBtn}
                                  onClick={() => removeAttachment(a.id, att)}
                                  title="Remove"
                                >
                                  <Icon name="close" style={{ fontSize: 14 }} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                        {Object.entries(uploads).filter(([, u]) => u.annotationId === a.id).map(([k, u]) => (
                          <div key={k} className={styles.attachRow}>
                            <Icon name="upload" className={styles.attachIcon} />
                            <div className={styles.attachUpload}>
                              <span className={styles.attachUploadName}>{u.name}</span>
                              <div className={styles.attachProgress}>
                                <div className={styles.attachProgressFill} style={{ width: `${u.pct}%` }} />
                              </div>
                            </div>
                            <span className={styles.attachSize}>{u.pct}%</span>
                          </div>
                        ))}
                        {a.attachments.length === 0 && !Object.values(uploads).some((u) => u.annotationId === a.id) && (
                          <p className={styles.attachEmpty}>{isShare ? 'No files attached' : 'No PDFs attached yet'}</p>
                        )}
                        {attachError && <p className={styles.attachError}>{attachError}</p>}
                      </div>
                      <div className={styles.attachBlock}>
                        <div className={styles.attachHeader}>
                          <span className={styles.attachLabel}>Linked files</span>
                          {!isShare && projectId && linkPickerFor !== a.id && (
                            <button className={styles.attachAddBtn} onClick={() => setLinkPickerFor(a.id)}>
                              <Icon name="link" style={{ fontSize: 13 }} />
                              Link file
                            </button>
                          )}
                        </div>
                        {a.linkedFiles.map((f) => (
                          <div key={f.id} className={styles.attachRow}>
                            <Icon name={linkedFileIcon(f.type)} className={styles.attachIcon} />
                            <button className={styles.attachName} onClick={() => openLinkedFile(f)} title={`Open ${f.originalName}`}>
                              {f.originalName}
                            </button>
                            <span className={styles.attachSize}>{(f.extension || '').toUpperCase()}</span>
                            {!isShare && (
                              <button className={styles.attachIconBtn} onClick={() => unlinkFile({ variables: { annotationId: a.id, fileId: f.id } })} title="Unlink">
                                <Icon name="close" style={{ fontSize: 14 }} />
                              </button>
                            )}
                          </div>
                        ))}
                        {a.linkedFiles.length === 0 && linkPickerFor !== a.id && (
                          <p className={styles.attachEmpty}>No linked files</p>
                        )}
                        {linkPickerFor === a.id && projectId && (
                          <NoteFilePicker
                            projectId={projectId}
                            excludeIds={[...a.linkedFiles.map((f) => f.id), ...(fileId ? [fileId] : [])]}
                            busy={linkingFile}
                            onPick={(f) => linkFile({ variables: { annotationId: a.id, fileId: f.id } })}
                            onClose={() => setLinkPickerFor(null)}
                          />
                        )}
                      </div>
                      {noteError && <p className={styles.attachError}>{noteError}</p>}
                      {canWriteNotes && (
                        <>
                          {isShare && (
                            <input
                              className={styles.textarea}
                              placeholder="Your name (shown on the reply)"
                              value={guestName}
                              maxLength={60}
                              onChange={(e) => updateGuestName(e.target.value)}
                            />
                          )}
                          <textarea
                            className={styles.textarea}
                            placeholder="Add a reply…"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            rows={2}
                          />
                          <div className={styles.btnRow}>
                            {!isShare && (
                              <button className={styles.btnGhost} onClick={() => resolveAnnotation({ variables: { annotationId: a.id } })}>
                                {a.status === 'resolved' ? 'Reopen' : 'Resolve'}
                              </button>
                            )}
                            <div style={{ flex: 1 }} />
                            {!isShare && (
                              <button className={styles.btnDanger} onClick={() => deleteAnnotation({ variables: { annotationId: a.id } })} title="Delete">
                                <Icon name="delete" style={{ fontSize: 14 }} />
                              </button>
                            )}
                            <button
                              className={styles.btnPrimary}
                              onClick={() => handleReply(a.id)}
                              disabled={!replyText.trim() || replying}
                            >
                              Reply
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
  );

  const sceneNoteIndex = annotations.findIndex((a) => a.id === expandedId);
  const scenePanelOpen = sceneMode && (annotationMode || !!pendingPos || (sceneNoteIndex >= 0 && showAnnotations));

  return (
    <div className={`${styles.container} ${embedded ? styles.containerEmbedded : ''}`}>
      <header className={styles.topbar}>
        {!embedded && (
          <button
            className={styles.backBtn}
            onClick={() => navigate(isShare ? `/share/${shareToken}` : `/projects/${projectId}/gaussian`)}
            title="Back"
          >
            <Icon name="arrow_back" style={{ fontSize: 16 }} />
          </button>
        )}
        <div className={styles.brandText}>
          <span className={styles.brandLabel}>PHOTOREAL VIEW</span>
          <span className={styles.brandTitle}>
            Gaussian splat{zones.length > 0 ? ` · ${zones.length} zones` : ''}
          </span>
        </div>
        {sceneMode && canWriteNotes && showAnnotations && (
          <button
            className={`${styles.sceneActionBtn} ${annotationMode || pendingPos ? styles.sceneActionBtnOn : ''}`}
            disabled={!loaded}
            title={annotationMode || pendingPos ? 'Cancel the new note' : 'New note'}
            onClick={() => {
              if (annotationMode) { cancelAddNote(); return; }
              if (pendingPos) { setPendingPos(null); setPendingText(''); return; }
              setExpandedId(null);
              startAddNote();
            }}
          >
            <Icon name="add_comment" style={{ fontSize: 16 }} />
            Note
          </button>
        )}
      </header>

      <div className={styles.body}>
        {!sceneMode && (
        <aside className={styles.rail}>
          {zones.length > 0 && sceneUrl && (
            <>
              <div className={styles.railSectionHeader}>
                <span className={styles.railSectionLabel}>Scenes</span>
                <span className={styles.railSectionCount}>{zones.length + 1}</span>
              </div>
              <div
                className={`${styles.railRow} ${activeUrl === sceneUrl ? styles.railRowActive : ''}`}
                onClick={() => setActiveUrl(sceneUrl)}
              >
                <Icon name="public" className={styles.railRowIcon} />
                <span className={styles.railRowName}>Overview</span>
              </div>
              {zones.map((z) => (
                <div
                  key={z.label}
                  className={`${styles.railRow} ${activeUrl === z.url ? styles.railRowActive : ''}`}
                  onClick={() => setActiveUrl(z.url)}
                  title="Full-detail splat for this area"
                >
                  <Icon name="crop_free" className={styles.railRowIcon} />
                  <span className={styles.railRowName}>{z.label}</span>
                </div>
              ))}
            </>
          )}

          <div
            className={`${styles.railSectionHeader} ${styles.railSubHeaderClickable}`}
            onClick={() => setNotesCollapsed((c) => !c)}
          >
            <span className={styles.railSectionLabel}>Notes</span>
            <span className={styles.railSectionCount}>{annotations.length}</span>
            {canWriteNotes && showAnnotations && !annotationMode && !pendingPos && (
              <button
                className={styles.railMiniBtn}
                title="New note"
                disabled={!loaded}
                onClick={(e) => { e.stopPropagation(); setNotesCollapsed(false); startAddNote(); }}
              >
                <Icon name="add" style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
          {!notesCollapsed && (
            <>
              {!showAnnotations && (
                <p className={styles.railEmpty}>Notes live on the Overview scene — switch back to see or place them.</p>
              )}
              {noteFormBlock}
              {showAnnotations && annotations.length === 0 && !pendingPos && !annotationMode && (
                <p className={styles.railEmpty}>
                  {canWriteNotes
                    ? <>Click <strong>+</strong>, then click the scene to drop a marker.</>
                    : 'No notes on this splat.'}
                </p>
              )}
              {showAnnotations && annotations.map(renderNoteCard)}
            </>
          )}
        </aside>
        )}

        <div className={styles.viewerPane}>
          {scenePanelOpen && (
            <div className={styles.scenePanel}>
              <div className={styles.scenePanelHead}>
                <span className={styles.railSectionLabel}>{annotationMode || pendingPos ? 'New note' : 'Note'}</span>
                <div style={{ flex: 1 }} />
                <button
                  className={styles.railMiniBtn}
                  title="Close"
                  onClick={() => {
                    if (annotationMode) cancelAddNote();
                    setPendingPos(null);
                    setPendingText('');
                    setExpandedId(null);
                  }}
                >
                  <Icon name="close" style={{ fontSize: 14 }} />
                </button>
              </div>
              {noteFormBlock}
              {!annotationMode && !pendingPos && sceneNoteIndex >= 0 && renderNoteCard(annotations[sceneNoteIndex], sceneNoteIndex)}
            </div>
          )}
          {annotationMode && (
            <div className={styles.annotModeBanner}>
              <Icon name="ads_click" style={{ fontSize: 15 }} />
              Click on the scene to place your note — Esc or Cancel to stop
            </div>
          )}
          {error ? (
            <div className={styles.stateBox}>
              <Icon name="blur_on" style={{ fontSize: 40 }} />
              <p>{error}</p>
            </div>
          ) : activeUrl ? (
            <iframe
              key={activeUrl}
              ref={iframeRef}
              className={styles.iframeFill}
              src={`/gs-viewer.html?v=1019&url=${encodeURIComponent(activeUrl)}${splatViewerSwitches(window.location.search)}`}
              title="Photoreal view"
              allow="fullscreen"
            />
          ) : (
            <div className={styles.stateBox}>
              <Icon name="refresh" className="animate-spin" style={{ fontSize: 28 }} />
              <p>Loading…</p>
            </div>
          )}
        </div>
      </div>

      {!isShare && (
        <input
          ref={attachInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            const target = attachTargetRef.current;
            if (target && e.target.files?.length) uploadAttachments(target, e.target.files);
            e.target.value = '';
          }}
        />
      )}

      {viewing && (() => {
        const pdf = isPdfAttachment(viewing);
        return (
          <div className={styles.lightbox} onClick={() => setViewing(null)}>
            <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
              <div className={styles.lightboxBar}>
                <Icon name={pdf ? 'picture_as_pdf' : 'draft'} className={styles.attachIcon} />
                <span className={styles.lightboxName} title={viewing.fileName}>{viewing.fileName}</span>
                <span className={styles.attachSize}>
                  {formatBytes(viewing.fileSize)}
                  {viewing.uploadedBy ? ` · ${viewing.uploadedBy.firstName} ${viewing.uploadedBy.lastName}` : ''}
                </span>
                <div style={{ flex: 1 }} />
                {viewing.downloadUrl && (
                  <a className={styles.lightboxBtn} href={viewing.downloadUrl}>
                    <Icon name="download" style={{ fontSize: 15 }} />
                    Download
                  </a>
                )}
                <button className={styles.lightboxBtn} onClick={() => setViewing(null)} title="Close (Esc)">
                  <Icon name="close" style={{ fontSize: 15 }} />
                </button>
              </div>
              <div className={styles.lightboxBody}>
                {!viewing.url ? (
                  <p className={styles.lightboxNoPreview}>Preview needs S3 storage.</p>
                ) : pdf ? (
                  <iframe className={styles.lightboxFrame} src={viewing.url} title={viewing.fileName} />
                ) : (
                  <div className={styles.lightboxNoPreview}>
                    <Icon name="draft" style={{ fontSize: 40 }} />
                    <p>No preview for this file type — download it to open.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
});

export default SplatViewer;
